import React, { useState, useEffect } from 'react';
import { Table as TableIcon, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { datasetApi } from '../../api/datasetApi';
import { showErrorToast, showSuccessToast } from '../../lib/toast';
import SchemaTab from './DatasetExplorer/SchemaTab';
import SampleTab from './DatasetExplorer/SampleTab';
import InfoTab from './DatasetExplorer/InfoTab';

const PreviewTable = ({ dataset, onClose, onMetadataUpdate }) => {
  const [activeTab, setActiveTab] = useState('schema');
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!dataset) return;

    const fetchMetadata = async () => {
      setIsLoading(true);
      try {
        const data = await datasetApi.getDatasetMetadata(dataset.id);
        setMetadata(data);
      } catch (err) {
        console.error('Failed to fetch dataset metadata:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetadata();
  }, [dataset]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!dataset) return null;

  const handleUpdateMetadata = async (updatedData) => {
    try {
      const result = await datasetApi.updateDatasetMetadata(dataset.id, updatedData);
      setMetadata(prev => ({ ...prev, ...result }));
      onMetadataUpdate?.();
      showSuccessToast('Metadata saved.');
    } catch (err) {
      console.error('Failed to update metadata:', err);
      showErrorToast('Could not save metadata. Please try again.');
    }
  };

  const handleRebuildMetadata = async () => {
    try {
      const result = await datasetApi.rebuildMetadata(dataset.id);
      setMetadata(result);
      onMetadataUpdate?.();
      showSuccessToast('Metadata refreshed.');
    } catch (err) {
      console.error('Failed to rebuild metadata:', err);
      showErrorToast('Could not refresh metadata. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8 lg:p-12">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-[40px] shadow-2xl w-full max-w-7xl h-full max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200 overflow-hidden transition-colors">
        <div className="p-8 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between shrink-0 bg-white dark:bg-gray-950 transition-colors">
          <div className="flex items-center gap-6">
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl">
              <TableIcon size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter" title={dataset.name}>
                Data Sources: {dataset.type === 'bigquery' && dataset.name.includes('.')
                  ? dataset.name.split('.').pop()
                  : dataset.name}
              </h3>
            </div>
            <div className="h-10 w-[1px] bg-gray-100 dark:bg-gray-800 mx-2" />
            <div className="flex items-center h-full gap-2">
              {[
                { id: 'schema', label: 'Table Description' },
                { id: 'sample', label: 'Sample Data' },
                { id: 'info', label: 'Metadata Info' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    activeTab === tab.id
                      ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/20"
                      : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-all text-gray-400 hover:text-gray-900 dark:hover:text-white active:scale-90"
          >
            <X size={24} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden p-10 bg-gray-50/30 dark:bg-gray-900/10">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Hydrating Source Data...</span>
              </div>
            </div>
          ) : (
            <div className="h-full bg-white dark:bg-gray-950 rounded-3xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm transition-colors">
              {activeTab === 'schema' && (
                <SchemaTab
                  schema={metadata?.schema}
                  summary={metadata?.summary}
                  isFullScreen
                  onUpdateMetadata={handleUpdateMetadata}
                  onRebuildMetadata={handleRebuildMetadata}
                  spatialColumns={metadata?.spatial_columns}
                  temporalColumns={metadata?.temporal_columns}
                />
              )}
              {activeTab === 'sample' && (
                <SampleTab samples={metadata?.samples} isFullScreen />
              )}
              {activeTab === 'info' && (
                <InfoTab dataset={{ ...dataset, ...metadata }} isFullScreen />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewTable;
